import { SidePanelDS, Spacer, TextCapitalized } from "@qivia/ui";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { formatHour } from "../../../../libSlice/materialUiFormats";
import { ElectricCharge } from "../electricChargingAPI";
import "../../../../i18n/i18n";

const chargeDetails = [
  "type",
  "totalTime",
  "totalEnergy",
  "locationName",
  "locationAddress",
  "locationCity",
  "locationPostalCode",
] as const;

type ChargeDetail = (typeof chargeDetails)[number];

export const ChargeDetails = (props: { charge: ElectricCharge }) => {
  const { t } = useTranslation();
  const { charge } = props;

  const findValueToDisplay = useCallback(
    (detail: ChargeDetail) => {
      switch (detail) {
        case "type":
          return charge.type ? t(`electric_charging.type.${charge.type}`) : "";
        case "totalTime":
          return charge.totalTime !== null
            ? formatHour(charge.totalTime / 60)
            : "";
        case "totalEnergy":
          return charge.totalEnergy !== null
            ? `${charge.totalEnergy} kWh`
            : "";
        default:
          return charge[detail];
      }
    },
    [charge, t],
  );

  return (
    <>
      <TextCapitalized>
        <StyledLabel>
          {t(`electric_charging.sidePanel.details.title`)}
        </StyledLabel>
      </TextCapitalized>
      {chargeDetails.map((detail) => {
        const valueToDisplay = findValueToDisplay(detail);

        return (
          <div key={detail}>
            <Spacer y={2} />
            <SidePanelDS.SectionRow
              label={
                <StyledInformations>
                  <TextCapitalized>
                    {t(`electric_charging.sidePanel.details.${detail}`)}
                  </TextCapitalized>
                </StyledInformations>
              }
              value={
                <StyledInformations>
                  {valueToDisplay ? (
                    <TextCapitalized>{valueToDisplay}</TextCapitalized>
                  ) : (
                    "-"
                  )}
                </StyledInformations>
              }
            />
          </div>
        );
      })}
    </>
  );
};

const StyledLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledInformations = styled.div`
  display: flex;
  align-items: center;
  ${typographies["Body/L"]};
  color: ${colors["colors/text/black"]};
`;
